import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Check, Crown, Sparkles, Loader2, AlertCircle, X, Infinity, Heart, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function Subscription({ auth }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState({});
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Check for success redirect
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        fetch(`${API}/subscription/plans`, { credentials: 'include' }),
        fetch(`${API}/subscription/status`, { credentials: 'include' })
      ]);

      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(plansData);
      }

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setCurrentPlan(statusData);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please refresh the page.");
      return;
    }

    try {
      const response = await fetch(`${API}/subscription/checkout/status/${sessionId}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        toast.success("Welcome to the Inner Circle! Your subscription is now active.");
        // Refresh subscription status
        const statusRes = await fetch(`${API}/subscription/status`, { credentials: 'include' });
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setCurrentPlan(statusData);
        }
        // Clear URL params
        navigate('/subscription', { replace: true });
        return;
      } else if (data.status === 'expired') {
        toast.error("Payment session expired. Please try again.");
        navigate('/subscription', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error polling payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);

    try {
      const response = await fetch(`${API}/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout');
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
      setProcessingPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await fetch(`${API}/subscription/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel subscription');
      }

      const data = await response.json();
      toast.success(data.message);
      setShowCancelDialog(false);
      
      // Refresh subscription status
      const statusRes = await fetch(`${API}/subscription/status`, { credentials: 'include' });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setCurrentPlan(statusData);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading || sessionId) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 border-2 border-[#A87C6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6E6A66] font-light">
            {sessionId ? "Processing your payment..." : "Loading plans..."}
          </p>
        </div>
      </div>
    );
  }

  const isPremium = currentPlan?.plan !== 'free';
  const isAtLimit = currentPlan?.check_ins_used >= currentPlan?.check_in_limit && !isPremium;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="subscription-page">
      {/* Navigation */}
      <nav className="px-6 py-4 border-b border-[#E6E2DD]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/profile">
            <Button variant="ghost" className="rounded-full text-[#6E6A66] hover:text-[#2D2A26] hover:bg-[#E6E2DD]">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <span 
            className="text-xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Subscription
          </span>
          <div className="w-20"></div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Limit Warning Banner */}
        {isAtLimit && (
          <div className="mb-8 p-4 rounded-2xl bg-[#FEF3CD] border border-[#F5D77A] animate-fade-in">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#8B6914] font-medium">You've reached your weekly limit</p>
                <p className="text-[#A07D1A] text-sm mt-1">
                  You've used all {currentPlan?.check_in_limit} check-ins for this week. 
                  Join the Inner Circle for unlimited check-ins and deeper insights.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Usage Progress for Open Users */}
        {!isPremium && !isAtLimit && currentPlan?.check_ins_used > 0 && (
          <div className="mb-8 p-4 rounded-2xl bg-[#E6E2DD]/50 animate-fade-in">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6E6A66]">Weekly check-ins used</span>
              <span className="text-sm font-medium text-[#2D2A26]">
                {currentPlan.check_ins_used} / {currentPlan.check_in_limit}
              </span>
            </div>
            <div className="h-2 bg-[#E6E2DD] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#A87C6D] transition-all duration-500"
                style={{ width: `${(currentPlan.check_ins_used / currentPlan.check_in_limit) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-6">
            <Crown className="w-8 h-8 text-[#A87C6D]" />
          </div>
          <h1 
            className="text-3xl md:text-4xl font-light text-[#2D2A26] mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Choose your pace
          </h1>
          <p className="text-[#6E6A66] font-light max-w-md mx-auto">
            Whether you check in occasionally or every day, there's a plan that fits your rhythm.
          </p>
        </div>

        {/* Why Inner Circle Section - for non-Inner Circle users */}
        {!isPremium && (
          <div className="mb-12 animate-fade-in">
            <div className="paper-card rounded-3xl p-6 md:p-8 bg-gradient-to-br from-[#A87C6D]/5 to-transparent border border-[#A87C6D]/10">
              <h3 
                className="text-xl font-normal text-[#2D2A26] mb-3 text-center"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                The Inner Circle
              </h3>
              <p className="text-center text-[#6E6A66] font-light mb-6 max-w-lg mx-auto">
                For those who want deeper continuity, more personal language, and space to return to themselves as often as needed.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4">
                  <div className="w-10 h-10 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-3">
                    <Infinity className="w-5 h-5 text-[#A87C6D]" />
                  </div>
                  <p className="text-sm font-medium text-[#2D2A26]">Return</p>
                  <p className="text-xs text-[#6E6A66]">as often as needed</p>
                </div>
                <div className="text-center p-4">
                  <div className="w-10 h-10 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-3">
                    <Heart className="w-5 h-5 text-[#A87C6D]" />
                  </div>
                  <p className="text-sm font-medium text-[#2D2A26]">Deeper</p>
                  <p className="text-xs text-[#6E6A66]">attunement</p>
                </div>
                <div className="text-center p-4">
                  <div className="w-10 h-10 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="w-5 h-5 text-[#A87C6D]" />
                  </div>
                  <p className="text-sm font-medium text-[#2D2A26]">Personal</p>
                  <p className="text-xs text-[#6E6A66]">insights over time</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Plan Badge */}
        {isPremium && (
          <div className="text-center mb-8 animate-fade-in">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#A87C6D]/10 text-[#A87C6D]">
              <Sparkles className="w-4 h-4" />
              You're in the {plans[currentPlan.plan]?.name || 'Inner Circle'}
            </span>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 animate-slide-up">
          {/* Open Plan */}
          <div className={`paper-card rounded-3xl p-6 ${currentPlan?.plan === 'free' ? 'ring-2 ring-[#A87C6D]' : ''}`}>
            <h3 
              className="text-xl font-normal text-[#2D2A26] mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {plans.free?.name || 'Open'}
            </h3>
            <p className="text-sm text-[#A8A4A0] font-light mb-3">
              Complete, with intentional space
            </p>
            <div className="mb-4">
              <span className="text-3xl font-light text-[#2D2A26]">$0</span>
              <span className="text-[#6E6A66]">/month</span>
            </div>
            <ul className="space-y-3 mb-6">
              {(plans.free?.features || ["3 gentle check-ins per week", "Quick rituals for grounding", "Boundary scripts for everyday moments"]).map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[#6E6A66] font-light text-sm">
                  <Check className="w-4 h-4 text-[#7A9E7E] mt-0.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            {currentPlan?.plan === 'free' ? (
              <Button 
                disabled 
                className="w-full rounded-full py-5 bg-[#E6E2DD] text-[#6E6A66]"
              >
                Current plan
              </Button>
            ) : (
              <Button 
                variant="outline"
                className="w-full rounded-full py-5 border-[#E6E2DD] text-[#6E6A66]"
                disabled
              >
                Open tier
              </Button>
            )}
          </div>

          {/* Inner Circle Monthly */}
          <div className={`paper-card rounded-3xl p-6 relative ${currentPlan?.plan === 'premium_monthly' ? 'ring-2 ring-[#A87C6D]' : ''}`}>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="px-3 py-1 rounded-full bg-[#A87C6D] text-white text-xs font-medium">
                Most Popular
              </span>
            </div>
            <h3 
              className="text-xl font-normal text-[#2D2A26] mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Inner Circle
            </h3>
            <p className="text-sm text-[#A8A4A0] font-light mb-3">
              Monthly
            </p>
            <div className="mb-4">
              <span className="text-3xl font-light text-[#2D2A26]">${plans.premium_monthly?.price || '9.99'}</span>
              <span className="text-[#6E6A66]">/month</span>
            </div>
            <ul className="space-y-3 mb-6">
              {(plans.premium_monthly?.features || [
                "Unlimited check-ins — return as often as you need",
                "Deeper, more attuned responses",
                "Personal insights that evolve with you",
                "Custom affirmations after each session"
              ]).map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[#6E6A66] font-light text-sm">
                  <Check className="w-4 h-4 text-[#7A9E7E] mt-0.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            {currentPlan?.plan === 'premium_monthly' ? (
              <Button 
                disabled 
                className="w-full rounded-full py-5 bg-[#E6E2DD] text-[#6E6A66]"
              >
                Current plan
              </Button>
            ) : (
              <Button 
                onClick={() => handleSubscribe('premium_monthly')}
                disabled={processingPlan === 'premium_monthly'}
                className="w-full rounded-full py-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                data-testid="subscribe-monthly-btn"
              >
                {processingPlan === 'premium_monthly' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Join the Circle'
                )}
              </Button>
            )}
          </div>

          {/* Inner Circle Yearly */}
          <div className={`paper-card rounded-3xl p-6 ${currentPlan?.plan === 'premium_yearly' ? 'ring-2 ring-[#A87C6D]' : ''}`}>
            <h3 
              className="text-xl font-normal text-[#2D2A26] mb-1"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Early Inner Circle Annual
            </h3>
            <div className="mb-2">
              <span className="text-3xl font-light text-[#2D2A26]">${plans.premium_yearly?.price || '79.99'}</span>
              <span className="text-[#6E6A66]">/year</span>
              <span className="text-[#A87C6D] text-sm ml-2">— includes 4 months free</span>
            </div>
            <p className="text-xs text-[#A8A4A0] font-light italic mb-1">
              Available for a limited time
            </p>
            <p className="text-sm text-[#7A9E7E] font-light mb-4">
              A thank-you for joining us early.
            </p>
            <ul className="space-y-3 mb-6">
              {(plans.premium_yearly?.features || [
                "Unlimited check-ins — return as often as you need",
                "Deeper, more attuned responses",
                "Personal insights that evolve with you",
                "Custom affirmations after each session"
              ]).map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[#6E6A66] font-light text-sm">
                  <Check className="w-4 h-4 text-[#7A9E7E] mt-0.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            {currentPlan?.plan === 'premium_yearly' ? (
              <Button 
                disabled 
                className="w-full rounded-full py-5 bg-[#E6E2DD] text-[#6E6A66]"
              >
                Current plan
              </Button>
            ) : (
              <Button 
                onClick={() => handleSubscribe('premium_yearly')}
                disabled={processingPlan === 'premium_yearly'}
                variant="outline"
                className="w-full rounded-full py-5 border-[#A87C6D] text-[#A87C6D] hover:bg-[#A87C6D] hover:text-white"
                data-testid="subscribe-yearly-btn"
              >
                {processingPlan === 'premium_yearly' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Join the Circle'
                )}
              </Button>
            )}
          </div>
        </div>

        {/* FAQ / Info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-[#A8A4A0]">
            Cancel anytime. No questions asked. Your peace of mind comes first.
          </p>
        </div>

        {/* Cancel Subscription Section - Only for Inner Circle Users */}
        {isPremium && (
          <div className="mt-12 pt-8 border-t border-[#E6E2DD]">
            <div className="text-center">
              <h3 
                className="text-lg font-normal text-[#6E6A66] mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Need to take a break?
              </h3>
              <p className="text-sm text-[#A8A4A0] mb-4">
                You can cancel your subscription at any time. You'll keep access until the end of your billing period.
              </p>
              <Button
                onClick={() => setShowCancelDialog(true)}
                variant="ghost"
                className="text-[#A8A4A0] hover:text-[#C8553D] hover:bg-[#C8553D]/10"
                data-testid="cancel-subscription-btn"
              >
                Cancel subscription
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent className="bg-[#FDFBF7] border-[#E6E2DD]">
          <AlertDialogHeader>
            <AlertDialogTitle 
              className="text-xl font-normal text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Cancel your subscription?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6E6A66]">
              We're sorry to see you go. You'll keep access to Inner Circle features until the end of your current billing period. 
              You can always come back whenever you're ready.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-[#E6E2DD]">
              Keep subscription
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              disabled={cancelling}
              className="rounded-full bg-[#C8553D] hover:bg-[#B8452D] text-white"
            >
              {cancelling ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Yes, cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Footer />
    </div>
  );
}
